import { BooksActionItems } from "types/Models/books";
import { emptyApi } from "./emptyApi";
import { Entity } from "types/Models/entity";
import QueryString from "qs";
import { Form1099Item } from "types/Models/taxHome";

export type TaxActionItemType = {
  count: number;
  custom_type:
    | "INVOICES_DUE"
    | "REQUESTED_DOCUMENTS_CUSTOM_TYPE"
    | "DOCUMENT_REVIEW"
    | "ESIGN_REQUEST_CUSTOM_TYPE"
    | "SENSITIVE_DATA_CUSTOM_TYPE"
    | "FILING_IN_ADD_DETAILS_CUSTOM_TYPE"
    | "CART"
    | "PAYMENT_REQUESTED"
    | "REVIEW_AND_PAY"
    | "REQUESTED_ANSWERS_CUSTOM_TYPE"
    | "W_FORMS_PENDING"
    | "MERCHANT_EMAIL_REQUEST"
    | "PRICED_TICKET_CARD"
    | "STATE_REGISTRATIONS_DUE"
    | "STATE_REGISTRATION_MISSING_INFO"
    | "RA_EXPIRING";
  title: string;
  ids: string[];
  entity_id: string;
  order: number;
  subtitle: string;
  total_questions?: number | null;
  answered_questions?: number | null;
  state_id?: string | null;
};

type GetActionItemsQueryParams = {
  groupId: string;
  type: "TAX" | "BOOKS";
  card_status?: "REQUESTED" | "DEPRECATED" | "SUBMITTED";
  task_uuid?: string;
};

export type PendingSectionKey =
  | "PENDING_DOCUMENTS"
  | "OVERDUE"
  | "DUE_IN_2_WEEKS"
  | "STATE_COMPLIANCE"
  | "CART_PAYMENT_PENDING"
  | "DUE_LATER"
  | "INVOICES_DUE"
  | "W_FORMS_PENDING"
  | "FILING_IN_ADD_DETAILS_CUSTOM_TYPE";

export type DeadlineActionItem = {
  title: string;
  subtitle: string;
  custom_type: string;
  ids: string[];
  count: number;
  entity_id: string | null;
  entity_name?: string | null;
  state_id: string | null;
  total_questions: number | null;
  answered_questions: number | null;
  order: number | null;
};

export type PendingSection = {
  order: number;
  items_count: number;
  items: DeadlineActionItem[];
};

export type ActionItemsByDeadline = Partial<
  Record<PendingSectionKey, PendingSection>
>;

export type PendingActionItemCardStatus =
  | "REQUESTED"
  | "DEPRECATED"
  | "SUBMITTED";

export type PendingActionItem = {
  message_id: string;
  channel_id: string;
  custom_type: string;
  custom_data: Record<string, unknown>;
  card_status: PendingActionItemCardStatus;
  sender: {
    name: string;
    uuid?: string;
  };
  created_at: string;
};

export type PendingActionItemsResponse = {
  count: number;
  items: PendingActionItem[];
};

type GetActionItemsResponse<T extends "TAX" | "BOOKS"> = T extends "TAX"
  ? { tax_action_items: TaxActionItemType[] }
  : BooksActionItems;

export const actionItemsApi = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getActionItems: build.query<
      GetActionItemsResponse<GetActionItemsQueryParams["type"]>,
      GetActionItemsQueryParams
    >({
      query: ({ groupId, type, card_status, task_uuid }) => {
        let queryUrl = QueryString.stringify(
          { card_status, product: type, task_uuid },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `/api/inkle/company/group/${groupId}/action_items/${queryUrl}`,
        };
      },
      providesTags: () => [{ type: "ALL_ACTION_ITEMS" }],
    }),
    getForm1099List: build.query<Form1099Item[], { groupId: string }>({
      query: ({ groupId }) =>
        `/api/inkle/company/group/${groupId}/w-forms-pending/`,
    }),
    getActionItemsByDeadline: build.query<
      ActionItemsByDeadline,
      { groupId: string; taskUuid?: string; entityIds?: string[] }
    >({
      query: ({ groupId, taskUuid, entityIds }) => {
        const queryUrl = QueryString.stringify(
          {
            product: "TAX",
            task_uuid: taskUuid,
            // Omitted means the whole group, so an empty selection sends nothing.
            entity_ids: entityIds?.length ? entityIds.join(",") : undefined,
          },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `/api/inkle/company/group/${groupId}/action_items/by_deadline/${queryUrl}`,
        };
      },
      providesTags: () => [{ type: "ALL_ACTION_ITEMS" }],
    }),
    getPendingActionItems: build.query<
      PendingActionItemsResponse,
      { taskUuid: string }
    >({
      query: ({ taskUuid }) =>
        `/api/inkle/tasks/${taskUuid}/pending_action_items/`,
      providesTags: () => [{ type: "ALL_ACTION_ITEMS" }],
    }),
  }),
});

export const {
  useGetActionItemsQuery,
  useGetForm1099ListQuery,
  useGetActionItemsByDeadlineQuery,
  useGetPendingActionItemsQuery,
} = actionItemsApi;
